/**
 * HydrationManager.js - SSR and Client Hydration Management
 * ----------------------------------------------------------
 * Provides a unified system to detect and manage hydration state across
 * server-side and client-side environments. Ensures that DOM-dependent
 * operations are deferred until the client-side is fully ready, preventing
 * errors in SSR and partially rendered pages.
 * Includes minimal error tracking for critical hydration failures.
 *
 * Responsibilities:
 * - Detect server vs. browser environment safely
 * - Track and control hydration state (`isHydrated`)
 * - Defer DOM operations until after `DOMContentLoaded` (or timeout fallback)
 * - Queue operations during pre-hydration phase and execute them after hydration
 * - Expose readiness checks and fallback messages for SSR logic
 * - Minimal error tracking for critical hydration failures
 *
 * Features:
 * - `initialize()`                : starts hydration tracking and sets up DOM readiness hooks
 * - `waitForHydration()`          : queues operations until safe to execute on the client
 * - `isReady()`                   : returns true only if client is hydrated and DOM is ready
 * - `getServerSideFallback()`     : provides a dummy result for SSR-safe fallback
 * - `destroy()`                   : clears hydration state and pending ops (useful for teardown)
 *
 * Error Handling:
 * - Uses ErrorHandler for essential hydration failure tracking
 * - Tracks only critical failures to avoid code bloat
 * - Graceful fallback when ErrorHandler is unavailable
 * - Categories: 'hydration-init', 'operation-execution', 'timeout-handling'
 */

import DebugHelper from './DebugHelper.js';
import ErrorHandlerModule from '../../utils/ErrorHandler.js';

const isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined';

export default class HydrationManager {
  constructor() {
    this.isHydrated = false;
    this.pendingOperations = new Map();
    this.logger = DebugHelper.createContext('HydrationManager');
    this.hydrationTimeout = 5000;
    this._initErrorHandler();
  }

  /** Initialize error handler */
  _initErrorHandler() {
    try {
      this._errorHandler = new ErrorHandlerModule({ module: 'HydrationManager', maxErrors: 10 });
    } catch {
      this._errorHandler = {
        handleError:       () => {},
        handleFatalError:  () => {},
        getRecentErrors:   () => [],
        hasFatalErrors:    () => false,
        getErrorStats:     () => ({ totalErrors: 0, fatalErrors: 0 })
      };
      console.warn('HydrationManager: ErrorHandler not available, using fallback');
    }
  }

  /** Start hydration tracking */
  initialize() {
    if (!isBrowser) {
      this.logger.debug('Running in server-side environment');
      return;
    }
    try {
      this._initializeClientSide();
    } catch (error) {
      this._errorHandler.handleFatalError(
        'Critical error during HydrationManager initialization',
        error,
        { category: 'hydration-init' }
      );
      this.logger.error('Failed to initialize HydrationManager');
    }
  }

  /** Setup DOMContentLoaded and timeout */
  _initializeClientSide() {
    const initHandler = () => this._handleClientSideInit();

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initHandler, { once: true });
      setTimeout(() => {
        if (!this.isHydrated) {
          this.logger.warn('Hydration timeout reached, forcing initialization');
          try {
            initHandler();
          } catch (timeoutError) {
            this._errorHandler.handleError(
              'Error during hydration timeout handling',
              timeoutError,
              { category: 'timeout-handling' }
            );
            this.logger.error('Failed to handle hydration timeout');
          }
        }
      }, this.hydrationTimeout);
    } else {
      initHandler();
    }
  }

  /** Mark hydrated and run queued ops */
  _handleClientSideInit() {
    try {
      this.isHydrated = true;
      this.logger.debug('Client-side hydration completed');
      this._executePendingOperations();
    } catch (error) {
      this._errorHandler.handleError(
        'Error during client-side init handling',
        error,
        { category: 'hydration-init', pendingOperationsCount: this.pendingOperations.size }
      );
      this.logger.error('Failed to complete client-side hydration');
    }
  }

  /** Execute queued operations */
  _executePendingOperations() {
    const operations = Array.from(this.pendingOperations.values());
    this.pendingOperations.clear();
    let success = 0, fail = 0;
    operations.forEach(op => {
      try {
        this._executeOperation(op);
        success++;
      } catch (e) {
        fail++;
        this._errorHandler.handleError(
          'Failed to execute pending operation',
          e,
          { category: 'operation-execution', operationId: op.id }
        );
        this.logger.error(`Failed pending op: ${op.id}`);
      }
    });
    this.logger.debug(`Executed ${success}/${operations.length} pending operations (${fail} failed)`);
  }

  /** Run single operation */
  _executeOperation({ callback, resolve, reject, id }) {
    try {
      const result = callback();
      if (resolve) resolve(result);
    } catch (error) {
      this._errorHandler.handleError(
        'Operation execution failed',
        error,
        { category: 'operation-execution', operationId: id }
      );
      if (reject) reject(error);
    }
  }

  /** True if SSR */
  isServerSide() {
    try {
      return !isBrowser;
    } catch (e) {
      this._errorHandler.handleError(
        'Error detecting server-side environment',
        e,
        { category: 'hydration-init' }
      );
      return true;
    }
  }

  /** Hydration readiness */
  isReady() {
    try {
      return isBrowser && this.isHydrated;
    } catch (e) {
      this._errorHandler.handleError(
        'Error checking readiness',
        e,
        { category: 'hydration-init' }
      );
      return false;
    }
  }

  /** SSR fallback result */
  getServerSideFallback() {
    return { success: false, deferred: true, message: 'Deferred until client-side hydration', environment: 'server' };
  }

  /** Queue or run operation */
  waitForHydration(callback, operationId = null) {
    return new Promise((resolve, reject) => {
      try {
        if (this.isServerSide()) {
          resolve(this.getServerSideFallback());
        } else if (this.isHydrated) {
          this._executeOperationAsync(callback, resolve, reject);
        } else {
          const id = operationId || `op_${Date.now()}_${Math.random().toString(36).substr(2,9)}`;
          this.pendingOperations.set(id, { callback, resolve, reject, id });
        }
      } catch (e) {
        this._errorHandler.handleError('Error in waitForHydration', e, { category: 'operation-execution', operationId });
        reject(e);
      }
    });
  }

  /** Async callback exec */
  async _executeOperationAsync(callback, resolve, reject) {
    try {
      const result = await callback();
      resolve(result);
    } catch (e) {
      this._errorHandler.handleError('Async operation failed', e, { category: 'operation-execution' });
      reject(e);
    }
  }

  /** Clear state */
  destroy() {
    const count = this.pendingOperations.size;
    this.pendingOperations.clear();
    this.isHydrated = false;
    this.logger.debug(`Destroyed HydrationManager, cleared ${count} pending ops`);
  }

  // Diagnostic methods
  getRecentErrors(minutes = 5) { return this._errorHandler.getRecentErrors(minutes); }
  hasCriticalIssues()      { return this._errorHandler.hasFatalErrors(); }
  getErrorStats()          { return this._errorHandler.getErrorStats(); }
  resetErrors()            { if (this._errorHandler.clearErrors) this._errorHandler.clearErrors(); }

  /** Debug snapshot */
  getDebugInfo() {
    const stats = this._errorHandler.getErrorStats();
    return {
      isHydrated: this.isHydrated,
      isReady: this.isReady(),
      pendingCount: this.pendingOperations.size,
      timeout: this.hydrationTimeout,
      errorStats: stats,
      recentErrors: this.getRecentErrors().length
    };
  }
}
